import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  AssessmentType,
  ComplianceProgramState,
  GetComplianceProgramQuery,
  GetComplianceProgramQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import ComplianceProgramUserChangesDialog from "@apps/programs/components/compliance-program-user-changes-dialog";
import EngagementSurveyReport from "@apps/reporting/components/engagement-survey-report";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import AppLabel from "@components/draft-label/app-label";
import DraftLabel from "@components/draft-label/draft-label";
import Layout from "@components/layout/layout";
import AppLink, { useLink } from "@components/link/link";
import Loading from "@components/loading/loading";
import Tabs from "@components/tabs/tabs";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import usePkParams from "@helpers/hooks/use-pk-params";

import getComplianceProgramQuery from "../graphql/get-compliance-program-query";
import ComplianceProgramActionDropdown from "./compliance-program-action-dropdown";
import ComplianceProgramActions from "./compliance-program-actions";
import ComplianceProgramDetails from "./compliance-program-details";
import ComplianceProgramProgress from "./compliance-program-progress";
import ComplianceProgramReminders from "./compliance-program-reminders";
import AssessmentReportTab from "./program-reporting/assessment-report-tab";

enum ComplianceProgramDetailTabType {
  Actions = "My Actions",
  Progress = "Progress",
  Reporting = "Reporting",
  Reminders = "Reminders",
}

enum ComplianceProgramReportingTab {
  Manager = "manager",
  Upward = "upward",
  Engagement = "engagement",
}

type ComplianceProgramDetailTab = {
  type: ComplianceProgramDetailTabType;
  reportTab?: ComplianceProgramReportingTab;
};

export enum ProgramActionToDoStateFilter {
  All = "All Actions",
  ToDo = "To Do",
}

const ComplianceProgram = () => {
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const complianceProgramId = usePkParams("complianceProgramId");
  const { reportTab } = useParams<{
    reportTab?: ComplianceProgramReportingTab;
  }>();
  const url = `/programs/${complianceProgramId}/progress`;
  const [selectedTab, setSelectedTab] = useState<ComplianceProgramDetailTab>(
    location.pathname.includes("/actions")
      ? { type: ComplianceProgramDetailTabType.Actions }
      : location.pathname.includes("/reporting")
      ? { type: ComplianceProgramDetailTabType.Reporting, reportTab }
      : { type: ComplianceProgramDetailTabType.Progress }
  );
  const link = useLink();
  const [
    isShowingProgramUserChangesDialog,
    setIsShowingProgramUserChangesDialog,
  ] = useState(false);

  const { data: complianceProgramData, loading: isLoadingComplianceProgram } =
    useQuery<GetComplianceProgramQuery, GetComplianceProgramQueryVariables>(
      getComplianceProgramQuery,
      {
        fetchPolicy: "network-only",
        variables: { complianceProgramId },
      }
    );

  const complianceProgram = useMemo(
    () => complianceProgramData?.complianceProgram,
    [complianceProgramData]
  );

  useDocumentTitle(complianceProgram?.title || "Program Reporting");

  const handleChangeTab = (option: ComplianceProgramDetailTab) => {
    if (option.type === ComplianceProgramDetailTabType.Actions) {
      link.redirect(`/programs/${complianceProgramId}/actions`);
    } else if (option.type === ComplianceProgramDetailTabType.Reporting) {
      link.redirect(
        `/programs/${complianceProgramId}/reporting/${option.reportTab}`
      );
    } else if (option.type === ComplianceProgramDetailTabType.Reminders) {
      link.redirect(`/programs/${complianceProgramId}/reminders`);
    } else {
      link.redirect(`/programs/${complianceProgramId}/progress`);
    }
  };

  const programTabOptions = useMemo(() => {
    if (!complianceProgram) {
      return [];
    }

    const progress: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Progress,
    };
    const reminders: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Reminders,
    };
    const actions: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Actions,
    };
    const managerReporting: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Reporting,
      reportTab: ComplianceProgramReportingTab.Manager,
    };
    const upwardReporting: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Reporting,
      reportTab: ComplianceProgramReportingTab.Upward,
    };
    const engagementReporting: ComplianceProgramDetailTab = {
      type: ComplianceProgramDetailTabType.Reporting,
      reportTab: ComplianceProgramReportingTab.Engagement,
    };
    const hasMultipleReports =
      compact([
        complianceProgram.performanceAssessmentTemplate,
        complianceProgram.managerAssessmentTemplate,
        complianceProgram.engagementSurveyTemplate,
      ]).length > 1;
    return compact([
      {
        value: progress,
        label: ComplianceProgramDetailTabType.Progress,
      },
      complianceProgram.performanceAssessmentTemplate && {
        value: managerReporting,
        label: hasMultipleReports
          ? `Reporting (Manager ${label("review", { capitalize: true })})`
          : "Reporting",
      },
      complianceProgram.managerAssessmentTemplate && {
        value: upwardReporting,
        label: hasMultipleReports
          ? `Reporting (Upward ${label("review", { capitalize: true })})`
          : "Reporting",
      },
      complianceProgram.engagementSurveyTemplate && {
        value: engagementReporting,
        label: hasMultipleReports ? `Reporting (Survey)` : "Reporting",
      },
      {
        value: actions,
        label: ComplianceProgramDetailTabType.Actions,
      },
      complianceProgram.canUpdate?.permission && {
        value: reminders,
        label: ComplianceProgramDetailTabType.Reminders,
      },
    ]);
  }, [complianceProgram, label]);

  useEffect(() => {
    if (location.pathname.includes("/progress")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Progress });
    } else if (location.pathname.includes("/actions")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Actions });
    } else if (location.pathname.includes("/reminders")) {
      setSelectedTab({ type: ComplianceProgramDetailTabType.Reminders });
    } else if (location.pathname.includes("/reporting")) {
      setSelectedTab({
        type: ComplianceProgramDetailTabType.Reporting,
        reportTab,
      });
    }
  }, [location.pathname, reportTab]);

  useEffect(() => {
    if (
      complianceProgram?.canReadDetails &&
      !complianceProgram?.canReadDetails.permission
    ) {
      errorNotificationVar({
        title: "You do not have permission to view this program.",
        timeout: 10000,
      });
      link.redirect(`/programs`);
    }
  }, [complianceProgram?.canReadDetails, link]);

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={compact([
          { title: "Programs", url: "/programs" },
          complianceProgram && {
            title: complianceProgram.title,
            url: url,
          },
        ])}
      />
      <Layout.Container>
        {isLoadingComplianceProgram ? (
          <Layout.Main width="full">
            <Loading className="mt-12">Loading program...</Loading>
          </Layout.Main>
        ) : complianceProgram && complianceProgram.canReadDetails.permission ? (
          <Layout.Main width="full">
            <Layout.MainSection
              title={
                <div className="flex items-center gap-3">
                  <AppLink className="hover:underline" to={url}>
                    {complianceProgram.title}
                  </AppLink>
                  <div>
                    {complianceProgram.state ===
                      ComplianceProgramState.Draft && <DraftLabel />}
                    {complianceProgram.state ===
                      ComplianceProgramState.Paused && (
                      <AppLabel
                        label="Paused"
                        className="text-amber-900 bg-amber-200"
                      />
                    )}
                  </div>
                </div>
              }
              rightSide={
                <div className="flex items-center gap-4">
                  {complianceProgram.canUpdate?.permission && (
                    <Button
                      text="Edit"
                      to={`/programs/${complianceProgram.id}/edit`}
                    />
                  )}
                  <ComplianceProgramActionDropdown
                    complianceProgram={complianceProgram}
                  />
                </div>
              }
            >
              {complianceProgram.availableAppliesToUpdates?.hasUpdates && (
                <div className="flex text-sm items-center justify-between p-4 bg-amber-100 text-yellow-900 rounded-lg">
                  <div className="flex items-start flex-grow space-x-3">
                    <span className="text-amber-800 mt-0.5">
                      <TopicflowIcon type="warning" className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-gray-800 font-semibold">
                        {`It looks like there's been some changes to the
                        ${label("organization")}`}
                      </p>
                      <p className="text-sm">
                        To keep this program up to date, confirm the changes to
                        reflect them in your program.
                      </p>
                    </div>
                  </div>
                  <button
                    className="text-yellow-700 font-medium text-sm hover:underline pr-7"
                    onClick={() => setIsShowingProgramUserChangesDialog(true)}
                  >
                    View Changes
                  </button>
                </div>
              )}
              {isShowingProgramUserChangesDialog && (
                <ComplianceProgramUserChangesDialog
                  onClose={() => setIsShowingProgramUserChangesDialog(false)}
                  complianceProgram={complianceProgram}
                />
              )}
              <Layout.MainSubSection
                title="Program Details"
                expandedUiPreferenceKey="programReportingDetailsExpanded"
              >
                <ComplianceProgramDetails
                  complianceProgram={complianceProgram}
                />
              </Layout.MainSubSection>

              {complianceProgram.state === ComplianceProgramState.Published && (
                <>
                  <Tabs
                    className="mb-4"
                    options={programTabOptions}
                    selectedValue={selectedTab}
                    onClickOptionValue={handleChangeTab}
                  />

                  {selectedTab.type ===
                    ComplianceProgramDetailTabType.Actions && (
                    <ComplianceProgramActions
                      complianceProgramId={complianceProgram.id}
                    />
                  )}
                  {selectedTab.type ===
                    ComplianceProgramDetailTabType.Progress && (
                    <ComplianceProgramProgress
                      complianceProgramId={complianceProgram.id}
                    />
                  )}
                  {selectedTab.type ===
                    ComplianceProgramDetailTabType.Reporting &&
                    selectedTab.reportTab ===
                      ComplianceProgramReportingTab.Manager && (
                      <AssessmentReportTab
                        assessmentType={AssessmentType.Performance}
                        complianceProgramId={complianceProgram.id}
                      />
                    )}
                  {selectedTab.type ===
                    ComplianceProgramDetailTabType.Reporting &&
                    selectedTab.reportTab ===
                      ComplianceProgramReportingTab.Upward && (
                      <AssessmentReportTab
                        assessmentType={AssessmentType.Manager}
                        complianceProgramId={complianceProgram.id}
                      />
                    )}
                  {selectedTab.type ===
                    ComplianceProgramDetailTabType.Reporting &&
                    selectedTab.reportTab ===
                      ComplianceProgramReportingTab.Engagement && (
                      <EngagementSurveyReport
                        complianceProgramId={complianceProgram.id}
                      />
                    )}
                  {selectedTab.type ===
                    ComplianceProgramDetailTabType.Reminders && (
                    <ComplianceProgramReminders
                      complianceProgram={complianceProgram}
                      complianceProgramId={complianceProgram.id}
                    />
                  )}
                </>
              )}
            </Layout.MainSection>
          </Layout.Main>
        ) : (
          <Layout.Main width="full">
            <div className="p-6">Program not found.</div>
          </Layout.Main>
        )}
      </Layout.Container>
    </Layout>
  );
};

export default ComplianceProgram;
